import { isAbortError } from '@adv-frontend/error';
import type { AsyncFlow, AsyncFlowState } from '../../contract/async-flow';
import type { FlowSubscription } from '../../contract/subscription';
import { withBypassSsr } from '../../utils/bypassSsrCheck';
import { ComputedFlowBase } from '../base/instance';
import { AsyncFlowComputation } from './computation';

/**
 * Параметры конфигурации для создания AsyncComputedFlow.
 *
 * @typeParam T - Тип вычисляемого значения
 */
export interface AsyncComputedFlowOptions<T> {
    /**
     * Начальное состояние потока до первого завершенного вычисления.
     *
     * Используется в трех случаях:
     * - пока идет первое вычисление (нет завершенных), его `data` подставляется в
     *   `pending.data`, чтобы потребители сразу видели заглушку вместо `undefined`;
     * - если первое вычисление завершилось ошибкой, его `data` так же подставляется
     *   в `error.data`;
     * - если первое вычисление прервано через `ctx.skip()` и нет кешированного значения,
     *   поток принимает это состояние целиком.
     */
    initialValue?: AsyncFlowState<T>;

    /**
     * Функция для сравнения вычисленных значений, чтобы определить, изменились ли они.
     *
     * Используется для предотвращения ненужных обновлений, когда вычисленное значение
     * эквивалентно предыдущему значению, даже если они не строго равны.
     *
     * @param a - Предыдущее вычисленное значение
     * @param b - Новое вычисленное значение
     * @returns `true`, если значения считаются равными, `false` в противном случае
     * @default Object.is
     *
     * @example
     * ```typescript
     * // Пользовательское сравнение для объектов
     * equals: (a, b) => deepEqual(a, b)
     * ```
     */
    equals?: (a: T, b: T) => boolean;
}

/**
 * Абстрактный базовый класс для асинхронных вычисляемых потоков, которые автоматически пересчитывают свое значение
 * при изменении их зависимостей.
 *
 * @typeParam T - Тип значения, которое вычисляет и испускает этот поток
 *
 * @example
 * ```typescript
 * const userFlow = asyncComputedFlow(function* ({ watchAsync }) {
 *   return yield* watchAsync(userSourceFlow);
 * });
 *
 * // Получить текущее состояние
 * const state = userFlow.getSnapshot();
 *
 * // Ожидать завершения
 * const userData = await userFlow.asPromise();
 * ```
 */
export abstract class AsyncComputedFlowBase<T>
    extends ComputedFlowBase<AsyncFlowState<T>, AsyncFlowComputation<T>>
    implements AsyncFlow<T>
{
    /**
     * Параметры конфигурации для этого экземпляра потока
     */
    private options: AsyncComputedFlowOptions<T> | undefined;

    /**
     * Выполняемые в данный момент вычисления
     */
    private pendingComputations: AsyncFlowComputation<T>[] = [];

    /**
     * Последнее полностью завершенное вычисление, если есть
     */
    private lastFinishedComputation: AsyncFlowComputation<T> | null = null;

    /**
     * Счетчик поколений для вычислений. Используется для игнорирования устаревших вычислений,
     * когда несколько асинхронных операций выполняются одновременно
     */
    private epochCounter = 0;

    /**
     * Номер поколения последнего вычисления, которое успешно завершилось
     */
    private currentEpoch = 0;

    /**
     * Кэшированный promise, возвращаемый из asPromise()
     */
    private promise: Promise<T> | null = null;

    /**
     * Создает новый экземпляр AsyncComputedFlow.
     *
     * @param options - Необязательная конфигурация для этого вычисляемого потока
     */
    public constructor(options?: AsyncComputedFlowOptions<T>) {
        super();
        this.options = options;
    }

    /**
     * Возвращает промис, который разрешается с данными, когда асинхронный поток достигает состояния успеха,
     * или отклоняется с ошибкой, когда асинхронный поток достигает состояния ошибки.
     *
     * Если текущее состояние уже разрешено (успех или ошибка), промис разрешается/отклоняется немедленно.
     * Если текущее состояние - ожидание, метод подписывается на изменения состояния и ждет разрешения.
     *
     * @returns Promise, который разрешается с данными при успехе или отклоняется с ошибкой при неудаче
     *
     * @example
     * ```typescript
     * const userFlow = asyncComputedFlow(function* ({ watchAsync }) {
     *   return yield* watchAsync(userSourceFlow);
     * });
     *
     * // Будет ожидать разрешения потока
     * userFlow.asPromise()
     *   .then(data => console.log('Успех:', data))
     *   .catch(error => console.error('Ошибка:', error));
     * ```
     */
    public asPromise(): Promise<T> {
        // Состояние читаем до проверки кеша: getSnapshot() может запустить вычисление, а старт
        // вычисления сбрасывает закешированный promise.
        const initialState = this.getSnapshot();

        if (this.promise) {
            return this.promise;
        }

        let resolvePromise!: (data: T) => void;
        let rejectPromise!: (error: unknown) => void;
        const promise = new Promise<T>((resolve, reject) => {
            resolvePromise = resolve;
            rejectPromise = reject;
        });

        // Кладем promise в кеш до подписки, а не после: изнутри subscribe() поток может синхронно
        // пройти pending -> success -> pending, и сброс кеша на старте нового вычисления обязан
        // попасть в настоящий объект. Иначе сброс пришелся бы на еще не присвоенное поле, а в кеше
        // потом осел бы разрешенный promise потока, который снова pending. По той же причине
        // вызов, пришедший на этом синхронном переходе, получает этот же объект, а не второй promise.
        this.promise = promise;

        if (initialState.status === 'success') {
            resolvePromise(initialState.data);
            return promise;
        }

        if (initialState.status === 'error') {
            rejectPromise(initialState.error);
            return promise;
        }

        // Уведомление может прийти синхронно изнутри subscribe(), когда объекта подписки еще
        // нет: вычисляемый поток так догоняет нового слушателя изменившимися источниками.
        // Поэтому слушатель только помечает подписку ненужной, а снимает ее тот, у кого
        // объект уже есть.
        let subscription: FlowSubscription | null = null;
        let settled = false;

        const onChange = () => {
            const state = this.getSnapshot();

            if (state.status === 'pending') {
                return;
            }

            settled = true;
            subscription?.unsubscribe();

            if (state.status === 'error') {
                rejectPromise(state.error);
                return;
            }

            resolvePromise(state.data);
        };

        subscription = withBypassSsr(() => this.subscribe(onChange));

        if (settled) {
            subscription.unsubscribe();
        }

        // Возвращаем локальный объект, а не поле: поток мог синхронно начать новое вычисление и
        // сбросить кеш, но вызвавший asPromise() ждет именно этот promise.
        return promise;
    }

    /**
     * Возвращает последнее закешированное состояние асинхронного потока без запуска
     * вычисления.
     *
     * До первого вычисления возвращает `undefined`.
     *
     * @returns Закешированное состояние асинхронного потока либо `undefined`
     */
    public override getCachedSnapshot(): AsyncFlowState<T> | undefined {
        if (this.cachedComputation) {
            return this.cachedComputation.getValue();
        }
        if (this.lastFinishedComputation) {
            return this.lastFinishedComputation.getValue();
        }
        return undefined;
    }

    /**
     * Выполняет фактическое вычисление значения этого потока.
     *
     * @returns AsyncFlowComputation, содержащий вычисленное значение или состояние ошибки
     */
    protected compute(): AsyncFlowComputation<T> {
        this.epochCounter++;
        const computation = new AsyncFlowComputation<T>(this.epochCounter);

        const state: AsyncFlowState<T> = {
            status: 'pending',
            // до первого завершенного вычисления показываем данные из initialValue,
            // затем — данные последнего завершенного вычисления
            data: this.lastFinishedComputation
                ? this.lastFinishedComputation.getValue().data
                : this.options?.initialValue?.data,
        };
        computation.setValue(state);
        this.onComputationStarted(computation);

        return this.computeAsync(computation);
    }

    /**
     * Абстрактный метод, который подклассы должны реализовать для выполнения фактического асинхронного вычисления.
     *
     * @param computation - Контекст вычисления для использования в асинхронной операции
     * @returns AsyncFlowComputation, содержащий вычисленное значение или состояние ошибки
     */
    protected abstract computeAsync(computation: AsyncFlowComputation<T>): AsyncFlowComputation<T>;

    /**
     * Обрабатывает начало нового вычисления.
     *
     * @param computation - Только что начатое вычисление
     */
    protected onComputationStarted(computation: AsyncFlowComputation<T>): void {
        this.pendingComputations.at(-1)?.abort();
        this.pendingComputations.push(computation);

        if (this.cachedComputation && this.promise && this.cachedComputation.getValue().status !== 'pending') {
            this.promise = null;
        }
    }

    /**
     * Обрабатывает завершение вычисления.
     *
     * @param computation - Вычисление, которое завершилось
     */
    protected override onComputationFinished(computation: AsyncFlowComputation<T>): void {
        computation.finalize();
        this.removePending(computation);

        const isOutdated = this.currentEpoch > computation.epoch;
        if (isOutdated) {
            return;
        }

        this.currentEpoch = computation.epoch;
        this.lastFinishedComputation = computation;

        // Подписаться на новый список источников и отписаться от предыдущих источников
        super.onComputationFinished(computation);

        // Обновить данные в состоянии ожидания любого выполняющегося вычисления
        if (this.cachedComputation !== computation && this.cachedComputation?.getValue().status === 'pending') {
            this.cachedComputation.setValue({
                status: 'pending',
                data: computation.getValue().data,
            });
        }

        // Уведомить потребителей потока о завершении асинхронной операции. Уведомление идет мимо
        // гейта isDirty: гейт схлопывает только инвалидации, а завершение операции — состоявшийся
        // переход, и терять его нельзя. Вычисление, завершившееся синхронно внутри compute(), еще
        // не установлено в кеш — о его переходе сообщит onComputationInstalled().
        if (!this.isComputing) {
            this.notify();
        }
    }

    /**
     * Обрабатывает ошибки вычисления и возвращает соответствующее состояние.
     *
     * @param computation - Вычисление, которое завершилось с ошибкой
     * @param error - Ошибка, возникшая во время вычисления
     * @returns AsyncFlowComputation, содержащий вычисленное значение или состояние ошибки
     */
    protected handleComputationError(computation: AsyncFlowComputation<T>, error: unknown): AsyncFlowComputation<T> {
        if (isAbortError(error)) {
            const prevComputation = this.getPreviousComputation(computation);
            if (prevComputation) {
                return this.revertComputation(computation, prevComputation);
            }

            const state = this.options?.initialValue ?? {
                status: 'error',
                error,
            };
            computation.setValue(state);
        } else {
            computation.setValue({
                status: 'error',
                error,
                // до первого завершенного вычисления показываем данные из initialValue — так же,
                // как их подставляет compute() в pending
                data: this.lastFinishedComputation
                    ? this.lastFinishedComputation.getValue().data
                    : this.options?.initialValue?.data,
            });
        }

        this.onComputationFinished(computation);
        return computation;
    }

    /**
     * Находит самое последнее вычисление для возврата, когда текущее вычисление прервано.
     *
     * Этот метод проверяет два случая:
     * - Если есть выполняющееся вычисление, вернуться к нему
     * - Если есть ранее завершенное вычисление, вернуться к нему
     *
     * @param current - Текущее вычисление, которое прерывается
     * @returns Вычисление для возврата или null, если такого нет
     */
    protected getPreviousComputation(current: AsyncFlowComputation<T>): AsyncFlowComputation<T> | null {
        for (let i = this.pendingComputations.length - 1; i >= 0; i--) {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- индекс гарантировано существует внутри цикла
            const computation = this.pendingComputations[i]!;
            if (computation !== current) {
                return computation;
            }
        }

        return this.lastFinishedComputation;
    }

    /**
     * Возвращает текущее вычисление к предыдущему целевому вычислению.
     *
     * @param current - Вычисление, от которого возвращаемся
     * @param target - Вычисление, к которому возвращаемся
     * @returns Вычисление, оказавшееся в кеше: `target` либо лежащее там более свежее
     */
    private revertComputation(
        current: AsyncFlowComputation<T>,
        target: AsyncFlowComputation<T>
    ): AsyncFlowComputation<T> {
        current.finalize();
        this.removePending(current);

        // Материализованное состояние до отката: в кеше не обязательно лежит `current`
        const cachedBefore = this.cachedComputation;
        const previousState = cachedBefore?.getValue();

        target.updateSourcesValue();

        // В кеше может лежать вычисление новее `target`: расчет, завершившийся, пока `current`
        // ждал, либо поднятый прямо на чтении источников выше — вычисляемый
        // источник уведомляет на чтении, а слушатель вправе прочитать этот поток. Такое
        // состояние уже уехало потребителям, откатывать его нельзя.
        const installed = this.cachedComputation;
        if (installed && installed !== current && installed.epoch > target.epoch) {
            return installed;
        }

        this.cachedComputation = target;

        // Состояния сравниваем целиком по ссылке, как это делает onComputationInstalled: у отката
        // pending → pending статус тот же, но данные могли смениться — вычисление, завершившееся
        // раньше, успевает переписать `data` тому, что лежит в кеше.
        const isOutdated = this.currentEpoch > current.epoch;
        if (previousState !== target.getValue() && !isOutdated && !this.isComputing) {
            // Уведомить потребителей потока о возврате состояния — так же мимо гейта isDirty.
            // Откат внутри compute() еще не установлен в кеш, о нем сообщит onComputationInstalled().
            this.notify();
        }

        return target;
    }

    /**
     * Удаляет вычисление из списка ожидающих вычислений.
     *
     * @param computation - Вычисление для удаления из списка ожидающих
     */
    private removePending(computation: AsyncFlowComputation<T>): void {
        const index = this.pendingComputations.indexOf(computation);
        if (index > -1) {
            this.pendingComputations.splice(index, 1);
        }
    }

    /**
     * Создает состояние успеха для переданных данных, опционально переиспользуя предыдущее состояние,
     * если данные равны согласно пользовательской функции сравнения.
     *
     * @param data - Успешно вычисленные данные
     * @returns AsyncFlowState со статусом "success" и предоставленными данными
     */
    protected getSuccessValue(data: T): AsyncFlowState<T> {
        if (this.options?.equals && this.lastFinishedComputation) {
            const lastValue = this.lastFinishedComputation.getValue();
            if (lastValue.status === 'success' && this.options.equals(data, lastValue.data)) {
                return lastValue;
            }
        }

        return { status: 'success', data };
    }
}
